import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  inject,
  input,
  output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { QaiCallPathwayVideoComponent } from '@qai/ui';
import {
  AudioCallControlService,
  ISegmentProvider,
  ConversationState,
} from 'src/app/services/learner/audio-call-control.service';
import { CallStoreService } from '../../../library/call-store/call-store.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [QaiCallPathwayVideoComponent],
  selector: 'app-call-pathway-video',
  standalone: true,
  styleUrls: ['./call-pathway-video.component.css'],
  templateUrl: './call-pathway-video.component.html',
})
export class CallPathwayVideoComponent
  implements OnInit, AfterViewInit, OnDestroy, ISegmentProvider
{
  src = input.required<string>();
  autoplay = input<boolean>(false);
  loop = input<boolean>(false);
  startTime = input<number>(0);
  firstAttempt = input<boolean>(false);
  readonly enrollmentUuid = input.required<string>();
  readonly callLearnUuid = input<string>('');
  muteOnStart = input<boolean>(true);

  @Output() videoReady = new EventEmitter<{ durationMs: number }>(); // emits duration_ms once loadedmetadata fires
  videoPlay = output<void>();
  videoPause = output<void>();
  videoEnded = output<void>();
  videoTimeUpdate = output<{ posMs: number; durationMs: number }>();

  @ViewChild(QaiCallPathwayVideoComponent)
  private qaiVideo?: QaiCallPathwayVideoComponent;

  private readonly audioCallControlService = inject(AudioCallControlService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly callStore = inject(CallStoreService);
  protected readonly agentSpeaking = signal(false);
  private audioCallStarted = false;
  private isVideoPlaying = false;
  private pauseOnSpeakingSubscription?: Subscription;

  // State management for contextual voice segments
  readonly currentTimeBucket = signal<string>('0:00');
  readonly isLoaded = signal<boolean>(false);
  private currentPositionMs = 0;
  private currentTranscript = '';

  constructor() {
    // Reset state when src changes
    effect(() => {
      const src = this.src();
      this.isLoaded.set(false);
      this.audioCallStarted = false;
      this.currentTranscript = '';
      this.currentPositionMs = 0;
      this.currentTimeBucket.set('0:00');
      if (!src) {
        return;
      }
    });

    effect(() => {
      const partialTranscript = this.callStore.getSpeechTranscriptPartial();
      const isLoaded = this.isLoaded();

      if (isLoaded && partialTranscript?.trim()) {
        this.currentTranscript = partialTranscript.trim();
      }
    });
  }

  ngOnInit(): void {
    this.startAudioCallOnce();
    this.audioCallControlService.registerSegmentProvider(this);
    this.pauseOnSpeakingSubscription =
      this.audioCallControlService.onStateChange$.subscribe((state) => {
        const agentSpeaking = state === ConversationState.AGENT_SPEAKING;
        const anySpeaking =
          agentSpeaking || state === ConversationState.USER_SPEAKING;

        // Drive the overlay that blocks user interaction during coach speech
        this.agentSpeaking.set(agentSpeaking);

        // No audio overlap: pause video whenever any participant starts speaking
        if (this.isVideoPlaying && anySpeaking) {
          this.pauseVideo();
        }
      });
  }

  ngAfterViewInit(): void {
    const videoEl = this.qaiVideo?.videoElementRef?.nativeElement as
      | HTMLVideoElement
      | undefined;
    if (!videoEl) {
      return;
    }
    const onLoaded = () => {
      videoEl.removeEventListener('loadedmetadata', onLoaded);
      this.isLoaded.set(true);
      this.currentPositionMs = this.startTime() * 1000;
      this.currentTimeBucket.set(this.getCurrentTimeBucket());
      this.videoReady.emit({ durationMs: Math.round(videoEl.duration * 1000) });
    };
    videoEl.addEventListener('loadedmetadata', onLoaded);
  }

  ngOnDestroy(): void {
    this.pauseVideo();
    this.callStore.setIsAudioCallMode(false);
    this.audioCallControlService.unregisterSegmentProvider();
    this.pauseOnSpeakingSubscription?.unsubscribe();
    this.audioCallControlService.endCall().catch((err) => {
      console.error('Audio call end failed', err);
    });
  }

  public pauseVideo(): void {
    this.qaiVideo?.videoElementRef?.nativeElement?.pause();
  }

  protected handleVideoPlay(): void {
    if (this.audioCallControlService.isAgentSpeaking) {
      this.pauseVideo();
      return;
    }

    this.isVideoPlaying = true;
    this.videoPlay.emit();
    this.audioCallControlService.setMicMuted(true).catch((err) => {
      console.error('setMicMuted(true) on video play failed', err);
    });
  }

  protected handleVideoPause(): void {
    this.isVideoPlaying = false;
    this.videoPause.emit();
  }

  protected async handleVideoEnded(): Promise<void> {
    this.isVideoPlaying = false;
    this.videoEnded.emit();
  }

  protected handleVideoTimeUpdate(time: number): void {
    const durationSec =
      this.qaiVideo?.videoElementRef?.nativeElement?.duration || 0;
    const posMs = Math.round(time * 1000);
    this.currentPositionMs = posMs;

    if (this.isLoaded()) {
      this.currentTimeBucket.set(this.getCurrentTimeBucket());
    }

    this.videoTimeUpdate.emit({
      posMs,
      durationMs: Math.round(durationSec * 1000),
    });
  }

  private startAudioCallOnce(): void {
    if (this.audioCallStarted) {
      return;
    }

    this.audioCallStarted = true;
    this.callStore.setIsAudioCallMode(true);
    this.audioCallControlService
      .startCall()
      .then(() => {
        if (this.muteOnStart()) {
          return this.audioCallControlService.setMicMuted(true);
        }
        return;
      })
      .catch((err) => {
        console.error('Audio call start failed', err);
        this.audioCallStarted = false;
      });
  }

  private formatTime(posMs: number): string {
    const totalSeconds = Math.floor(posMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  private getCurrentTimeBucket(): string {
    const videoEl = this.qaiVideo?.videoElementRef?.nativeElement as
      | HTMLVideoElement
      | undefined;
    const currentPosMs = videoEl
      ? Math.round(videoEl.currentTime * 1000)
      : this.currentPositionMs;
    return this.formatTime(currentPosMs);
  }

  getUserSpokenResponse(): string {
    const transcript =
      this.currentTranscript.trim() ||
      (this.callStore.getSpeechTranscriptPartial() ?? '').trim() ||
      (this.callStore.getSpeechTranscript() ?? '').trim();
    if (!transcript) return '';

    const timeLabel = this.getCurrentTimeBucket();
    const userSpokenResponse = `${timeLabel} | ${transcript}`;
    // Clear transcript for next utterance
    this.currentTranscript = '';
    return userSpokenResponse;
  }

  getCallLearnUuid(): string {
    return this.callLearnUuid() ?? '';
  }
}
